import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import { useContracts } from '../../hooks/useContract';
import { useWallet } from '../../context/WalletContext';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatUsdt, parseUsdt } from '../../utils/format';

const RouterPool = () => {
  const { storage } = useContracts();
  const { account } = useWallet();
  const notify = useNotify();

  const [pool, setPool] = useState(ethers.BigNumber.from(0));
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState('');
  const [claiming, setClaiming] = useState(false);
  const runIdRef = useRef(0);

  const load = async () => {
    const myRun = ++runIdRef.current;
    setLoading(true);
    try {
      const p = await storage.routerPool();
      if (myRun !== runIdRef.current) return;
      setPool(p);
    } catch (_) { /* ignore */ }
    finally {
      if (myRun === runIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const claim = async () => {
    if (!amount) { notify.error('Enter amount to claim'); return; }
    const amt = parseUsdt(amount);
    if (amt.isZero()) { notify.error('Amount must be greater than 0'); return; }
    if (amt.gt(pool)) { notify.error('Amount exceeds pool balance'); return; }
    const dest = (destination || '').trim();
    if (!ethers.utils.isAddress(dest)) { notify.error('Invalid destination address'); return; }

    setClaiming(true);
    const loadingId = notify.loading('Claiming router pool...');
    try {
      const tx = await storage.claimRouterPoolTo(amt, dest);
      notify.dismiss(loadingId);
      const mining = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(mining);
      notify.success(`Claimed ${amount} USDT from router pool`);
      setAmount('');
      load();
    } catch (err) {
      notify.dismiss(loadingId);
      notify.error(parseError(err));
    } finally {
      setClaiming(false);
    }
  };

  const claimAll = () => setAmount(ethers.utils.formatUnits(pool, 18));
  const useConnectedWallet = () => setDestination(account || '');

  return (
    <Card title="Router Pool" subtitle="Owner claim from accumulated pool" glow>
      <div className="stat mb-md">
        <span className="stat-label">Available Balance</span>
        <span className="stat-value-lg">
          {loading ? '...' : formatUsdt(pool)}<span className="stat-unit">USDT</span>
        </span>
      </div>

      <div className="input-group">
        <label className="input-label">Destination Wallet</label>
        <div className="flex gap-sm">
          <input
            className="input font-mono"
            placeholder="0x..."
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
          />
          <button className="btn btn-secondary btn-sm" onClick={useConnectedWallet} type="button" disabled={!account}>
            Me
          </button>
        </div>
      </div>

      <div className="input-group">
        <label className="input-label">Amount to Claim</label>
        <div className="flex gap-sm">
          <input
            className="input"
            type="number"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step="0.0001"
            min="0"
          />
          <button className="btn btn-secondary btn-sm" onClick={claimAll} type="button" disabled={pool.isZero()}>
            Max
          </button>
        </div>
      </div>

      <button
        className="btn btn-primary btn-full"
        onClick={claim}
        disabled={claiming || pool.isZero() || !amount || !destination}
      >
        {claiming ? <><MiniLoader /> Claiming</> : 'Claim to Destination'}
      </button>
    </Card>
  );
};

export default RouterPool;
